/**
 * ⌘K.
 *
 * Three rules shape everything below:
 *
 *  1. **It never blocks on the network.** Recents, actions and navigation are
 *     local and paint on the same frame the dialog opens. Server results stream
 *     in underneath, and the previous set stays on screen while they do, so the
 *     list never collapses mid-keystroke.
 *  2. **It never offers a dead command.** Record actions only appear while a
 *     record is open; everything else does exactly what its label says.
 *  3. **The keyboard is the primary input.** ↑ ↓ move, ↵ runs, Esc closes, and
 *     the footer says so.
 */

import { Command, useCommandState } from 'cmdk';
import {
  ArrowRight,
  Building2,
  ClipboardList,
  Compass,
  Download,
  FileText,
  Keyboard,
  LifeBuoy,
  Layers,
  Link2,
  Moon,
  Search,
  Settings,
  StickyNote,
  Sun,
  Tag as TagIcon,
  User as UserIcon,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import { PRIMARY_NAV, viewHref, viewIcon, type ShellIcon } from '@/components/app/Sidebar';
import { openShortcutSheet, SpecKbd } from '@/components/app/ShortcutSheet';
import { themeCommandLabel } from '@/components/app/ThemeToggle';
import { startTour } from '@/features/onboarding/TourOverlay';
import { CREATE_ACTIONS, DialogTitle, Kbd, toast } from '@/components/ui';
import type { CreateKind } from '@/components/ui';
import { api } from '@/lib/api';
import { ROUTES, stageMeta, tagColorMeta } from '@/lib/constants';
import { SHORTCUTS_BY_ID, useHotkeyScope, type ShortcutId } from '@/lib/keyboard';
import { useLivePipeline, useSavedViews, useSearch } from '@/lib/queries';
import { useTheme } from '@/lib/theme';
import { readLocalStorage, writeLocalStorage } from '@/lib/useLocalStorage';

/* ────────────────────────────────────────────────────────────────────────────
   Imperative open + the shell command bus
   ──────────────────────────────────────────────────────────────────────────── */

export const COMMAND_PALETTE_EVENT = 'af:palette';

export function openCommandPalette(): void {
  window.dispatchEvent(new CustomEvent(COMMAND_PALETTE_EVENT));
}

/**
 * Actions the shell can only *ask* for — the screen that owns the record does
 * the work. Feature screens listen for this and act on `action`.
 */
export const SHELL_COMMAND_EVENT = 'af:command';

export interface ShellCommandDetail {
  action: 'inquiry.new' | 'inquiry.assign' | 'inquiry.stage' | 'inquiry.note';
  inquiryId?: string;
}

export function emitShellCommand(detail: ShellCommandDetail): void {
  window.dispatchEvent(new CustomEvent<ShellCommandDetail>(SHELL_COMMAND_EVENT, { detail }));
}

/** How long the exit animation runs before the query is safe to clear. */
const EXIT_MS = 200;

/* ────────────────────────────────────────────────────────────────────────────
   Recents
   ──────────────────────────────────────────────────────────────────────────── */

const RECENT_KEY = 'af.palette.recent';
const RECENT_MAX = 6;

type RecentKind = 'inquiry' | 'contact' | 'company' | 'nav' | 'action';

interface RecentEntry {
  id: string;
  kind: RecentKind;
  label: string;
  sublabel?: string;
  to?: string;
}

const RECENT_ICON: Record<RecentKind, ShellIcon> = {
  inquiry: FileText,
  contact: UserIcon,
  company: Building2,
  nav: ArrowRight,
  action: ClipboardList,
};

function readRecents(): RecentEntry[] {
  const stored = readLocalStorage<RecentEntry[]>(RECENT_KEY, []);
  if (!Array.isArray(stored)) return [];
  return stored.filter(
    (entry): entry is RecentEntry =>
      Boolean(entry) && typeof entry.id === 'string' && typeof entry.label === 'string',
  );
}

function rememberRecent(entry: RecentEntry): void {
  const next = [entry, ...readRecents().filter((item) => item.id !== entry.id)].slice(0, RECENT_MAX);
  writeLocalStorage(RECENT_KEY, next);
}

/* ────────────────────────────────────────────────────────────────────────────
   Match highlighting
   ──────────────────────────────────────────────────────────────────────────── */

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function Highlight({ text, query }: { text: string; query: string }) {
  const term = query.trim();
  if (term.length < 2 || !text) return <>{text}</>;

  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, 'ig'));
  if (parts.length === 1) return <>{text}</>;

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <mark
            key={index}
            className="rounded-[var(--radius-xs)] bg-[var(--accent-soft)] px-px text-accent-text"
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Row
   ──────────────────────────────────────────────────────────────────────────── */

const ITEM_CLASS = [
  'group relative flex cursor-pointer select-none items-center gap-2.5 rounded-[var(--radius-sm)]',
  'px-2 py-1.5 text-base text-secondary',
  'transition-colors ease-[var(--ease-out)] duration-[var(--dur-instant)]',
  'data-[selected=true]:bg-[var(--surface-selected)] data-[selected=true]:text-primary',
  'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
].join(' ');

interface RowProps {
  id: string;
  icon: ShellIcon;
  /** Semantic tint (stage, tag) — otherwise the icon stays quiet. */
  iconColor?: string;
  label: ReactNode;
  sublabel?: ReactNode;
  trailing?: ReactNode;
  keywords: string[];
  onSelect: () => void;
}

function Row({ id, icon: Icon, iconColor, label, sublabel, trailing, keywords, onSelect }: RowProps) {
  return (
    <Command.Item
      value={id}
      keywords={keywords.filter(Boolean)}
      onSelect={onSelect}
      className={ITEM_CLASS}
    >
      <Icon
        className={
          iconColor
            ? 'size-4 shrink-0'
            : 'size-4 shrink-0 text-faint transition-colors ease-[var(--ease-out)] group-data-[selected=true]:text-secondary'
        }
        style={iconColor ? { color: iconColor } : undefined}
        strokeWidth={1.75}
        aria-hidden="true"
      />
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate">{label}</span>
        {sublabel ? <span className="mt-0.5 truncate text-xs text-faint">{sublabel}</span> : null}
      </span>
      {trailing ? (
        <span className="ml-auto flex shrink-0 items-center gap-1.5">{trailing}</span>
      ) : null}
    </Command.Item>
  );
}

/** A cmdk group; cmdk hides it automatically when everything inside is filtered out. */
function Group({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className={[
        // The `.eyebrow` treatment, expressed as utilities so Tailwind emits them.
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2.5',
        '[&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[length:var(--text-2xs)]',
        '[&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase',
        '[&_[cmdk-group-heading]]:tracking-[var(--tracking-label)] [&_[cmdk-group-heading]]:text-faint',
      ].join(' ')}
    >
      {children}
    </Command.Group>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Static command model
   ──────────────────────────────────────────────────────────────────────────── */

const NAV_SHORTCUT: Record<string, ShortcutId | undefined> = {
  inbox: 'goInbox',
  board: 'goBoard',
  contacts: 'goContacts',
  tasks: 'goTasks',
  analytics: 'goAnalytics',
};

interface ActionSpec {
  id: string;
  label: string;
  icon: ShellIcon;
  keywords: string[];
  /** Only offered while an inquiry is open. */
  needsRecord?: boolean;
  shortcut?: ShortcutId;
  run: () => void;
}

/* ────────────────────────────────────────────────────────────────────────────
   Palette
   ──────────────────────────────────────────────────────────────────────────── */

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The shell's create. The palette does NOT own a create of its own — it mounts
   * `CREATE_ACTIONS`, the same array the `New` button mounts, and routes every
   * pick through the same function, so the two entry points can never offer
   * different verbs or spell one of them differently.
   */
  onCreate: (kind: CreateKind) => void;
}

export function CommandPalette({ open, onOpenChange, onCreate }: CommandPaletteProps) {
  const navigate = useNavigate();
  const recordMatch = useMatch('/inbox/:id');
  const { theme, toggle } = useTheme();
  const views = useSavedViews();

  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState<RecentEntry[]>([]);

  // 120ms is under the perceptual "instant" threshold and still coalesces typing.
  const search = useSearch(query, { delay: 120, minLength: 2, enabled: open });
  const results = search.data;
  /* An inquiry row prints its stage's name and paints its icon the stage's
     `--stage-*` step, both through `stageMeta()`. The palette fetches no
     pipeline of its own, so it subscribes to the one the shell loaded rather
     than quietly rendering the seeded labels. */
  useLivePipeline();
  const term = query.trim();
  const searching = term.length >= 2;

  useHotkeyScope('command-palette', open);

  // Re-read on open: another tab may have written recents since last time.
  useEffect(() => {
    if (!open) return;
    setRecents(readRecents());
  }, [open]);

  const close = useCallback(() => {
    onOpenChange(false);
    window.setTimeout(() => setQuery(''), EXIT_MS);
  }, [onOpenChange]);

  const run = useCallback(
    (entry: RecentEntry, action: () => void) => {
      rememberRecent(entry);
      close();
      action();
    },
    [close],
  );

  const go = useCallback(
    (entry: RecentEntry, to: string) => run(entry, () => navigate(to)),
    [navigate, run],
  );

  const inquiryId = recordMatch?.params.id;

  const actions = useMemo<ActionSpec[]>(() => {
    const list: ActionSpec[] = [
      {
        id: 'action:assign',
        label: 'Assign owner',
        icon: UserPlus,
        keywords: ['assign', 'owner', 'rep', 'route'],
        needsRecord: true,
        shortcut: 'assign',
        run: () => emitShellCommand({ action: 'inquiry.assign', inquiryId }),
      },
      {
        id: 'action:stage',
        label: 'Change stage',
        icon: Layers,
        keywords: ['stage', 'pipeline', 'move', 'qualify'],
        needsRecord: true,
        shortcut: 'stage',
        run: () => emitShellCommand({ action: 'inquiry.stage', inquiryId }),
      },
      {
        id: 'action:note',
        label: 'Add note',
        icon: StickyNote,
        keywords: ['note', 'comment', 'log'],
        needsRecord: true,
        shortcut: 'note',
        run: () => emitShellCommand({ action: 'inquiry.note', inquiryId }),
      },
      {
        id: 'action:export',
        label: 'Export inquiries to CSV',
        icon: Download,
        keywords: ['export', 'csv', 'download', 'spreadsheet'],
        run: () => {
          void toast.promise(downloadInquiriesCsv(), {
            loading: 'Preparing export…',
            success: 'Export downloaded',
            error: 'The export could not be generated',
          });
        },
      },
      {
        id: 'action:theme',
        label: themeCommandLabel(theme),
        icon: theme === 'dark' ? Sun : Moon,
        keywords: ['theme', 'dark', 'light', 'appearance'],
        shortcut: 'toggleTheme',
        run: toggle,
      },
      {
        id: 'action:copy-link',
        label: 'Copy link to this page',
        icon: Link2,
        keywords: ['copy', 'link', 'url', 'share'],
        run: () => {
          void navigator.clipboard
            .writeText(window.location.href)
            .then(() => toast.success('Link copied'))
            .catch(() => toast.error('Could not copy the link'));
        },
      },
      {
        id: 'action:shortcuts',
        label: 'Keyboard shortcuts',
        icon: Keyboard,
        keywords: ['shortcuts', 'keys', 'help', 'keyboard'],
        shortcut: 'help',
        run: openShortcutSheet,
      },
      /* The keywords are the point of this row. Somebody who needs it types
         "help", "lost" or "how", not "tour" — the word they would only know
         after they had already found it. */
      {
        id: 'action:tour',
        label: 'Take the tour',
        icon: Compass,
        keywords: ['tour', 'help', 'lost', 'new', 'how', 'guide', 'onboarding', 'explain'],
        run: startTour,
      },
    ];

    return list.filter((action) => !action.needsRecord || Boolean(inquiryId));
  }, [inquiryId, navigate, theme, toggle]);

  const savedViews = views.data ?? [];
  const resultCount = results
    ? results.inquiries.length +
      results.contacts.length +
      results.companies.length +
      results.users.length +
      results.tags.length
    : 0;

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      label="Command palette"
      loop
      overlayClassName={[
        'fixed inset-0 z-[var(--z-overlay)] bg-[var(--scrim)] backdrop-blur-[2px]',
        'data-[state=open]:animate-[af-fade-in_var(--dur-normal)_var(--ease-out)]',
        'data-[state=closed]:animate-[af-fade-out_var(--dur-fast)_var(--ease-out)]',
      ].join(' ')}
      contentClassName={[
        'fixed left-1/2 top-[12vh] z-[var(--z-modal)] w-[min(640px,calc(100vw-var(--space-8)))]',
        '-translate-x-1/2 overflow-hidden rounded-[var(--radius-xl)]',
        'border border-line bg-[var(--surface-overlay)] shadow-[var(--shadow-4)] focus:outline-none',
        'data-[state=open]:animate-[af-scale-in_var(--dur-normal)_var(--ease-out)]',
        'data-[state=closed]:animate-[af-fade-out_var(--dur-fast)_var(--ease-out)]',
      ].join(' ')}
    >
      <DialogTitle className="sr-only">Command palette</DialogTitle>

      {/* Query */}
      <div className="relative flex h-12 shrink-0 items-center gap-2.5 border-b border-line px-3.5">
        <Search className="size-4 shrink-0 text-faint" strokeWidth={1.75} aria-hidden="true" />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          autoFocus
          placeholder="Search inquiries, contacts, companies — or type a command"
          className={[
            'h-full min-w-0 flex-1 bg-transparent text-md text-primary placeholder:text-faint',
            // The dialog itself carries the focus affordance; the field must not double it.
            'focus-visible:outline-none!',
          ].join(' ')}
        />
        {query ? null : <Kbd keys={['escape']} size="sm" />}
        {searching && search.isFetching ? (
          <span
            aria-hidden="true"
            className="skeleton absolute inset-x-0 bottom-0 h-[2px] rounded-none"
          />
        ) : null}
      </div>

      {/* Results */}
      <Command.List
        label="Results"
        className="max-h-[min(52vh,420px)] overflow-y-auto overscroll-contain p-1.5"
      >
        <Command.Empty>
          <div className="px-3 py-9 text-center">
            {/* `No <plural> match “<query>”` — the product's one headline for
                "the query on screen matched nothing". This overlay used to have
                two of its own, neither of which was that one. */}
            <p className="text-base text-secondary">
              {searching ? (
                <>
                  No results match <span className="font-medium text-primary">“{term}”</span>
                </>
              ) : (
                'No results match'
              )}
            </p>
            <p className="mt-1 text-xs text-faint">
              Try a reference like AF-2418, a company, or a person’s name.
            </p>
          </div>
        </Command.Empty>

        {!query && recents.length > 0 ? (
          <Group heading="Recent">
            {recents.map((entry) => (
              <Row
                key={entry.id}
                id={entry.id}
                icon={RECENT_ICON[entry.kind]}
                label={entry.label}
                sublabel={entry.sublabel}
                keywords={[entry.label, entry.sublabel ?? '']}
                onSelect={() => (entry.to ? go(entry, entry.to) : close())}
              />
            ))}
          </Group>
        ) : null}

        {/*
          CREATE — the shared array, not a retyped list.

          `UI-PRIMITIVES.md` has always said the palette and the `New` button
          mount the same `CREATE_ACTIONS`; the palette offered one verb of the
          five ("New inquiry") and named it differently from the menu that
          offered all five. Same array, same order, same words, same heading, and
          the same `onCreate` the button calls.
        */}
        <Group heading="Create">
          {CREATE_ACTIONS.map((action) => (
            <Row
              key={action.kind}
              id={`create:${action.kind}`}
              icon={action.icon}
              label={action.label}
              sublabel={action.description}
              keywords={['new', 'create', 'add', action.label, action.kind]}
              onSelect={() =>
                run(
                  { id: `create:${action.kind}`, kind: 'action', label: `Create ${action.label.toLowerCase()}` },
                  () => onCreate(action.kind),
                )
              }
            />
          ))}
        </Group>

        <Group heading={inquiryId ? 'Actions · this inquiry' : 'Actions'}>
          {actions.map((action) => (
            <Row
              key={action.id}
              id={action.id}
              icon={action.icon}
              label={action.label}
              keywords={action.keywords}
              trailing={
                action.shortcut ? (
                  <SpecKbd spec={SHORTCUTS_BY_ID[action.shortcut].spec} size="sm" />
                ) : null
              }
              onSelect={() => run({ id: action.id, kind: 'action', label: action.label }, action.run)}
            />
          ))}
        </Group>

        <Group heading="Go to">
          {PRIMARY_NAV.map((entry) => {
            const shortcut = NAV_SHORTCUT[entry.id];
            return (
              <Row
                key={entry.id}
                id={`nav:${entry.id}`}
                icon={entry.icon}
                label={entry.label}
                keywords={[entry.label, 'go', 'navigate']}
                trailing={
                  shortcut ? <SpecKbd spec={SHORTCUTS_BY_ID[shortcut].spec} size="sm" /> : null
                }
                onSelect={() =>
                  go(
                    { id: `nav:${entry.id}`, kind: 'nav', label: entry.label, to: entry.to },
                    entry.to,
                  )
                }
              />
            );
          })}
          <Row
            id="nav:settings"
            icon={Settings}
            label="Settings"
            keywords={['settings', 'preferences', 'admin', 'go']}
            trailing={<SpecKbd spec={SHORTCUTS_BY_ID.goSettings.spec} size="sm" />}
            onSelect={() =>
              go(
                { id: 'nav:settings', kind: 'nav', label: 'Settings', to: ROUTES.settings },
                ROUTES.settings,
              )
            }
          />
          <Row
            id="nav:getting-started"
            icon={LifeBuoy}
            label="Getting started"
            sublabel="What this app is, and what the words mean"
            keywords={['getting started', 'help', 'new', 'onboarding', 'glossary', 'words', 'lost']}
            onSelect={() =>
              go(
                {
                  id: 'nav:getting-started',
                  kind: 'nav',
                  label: 'Getting started',
                  to: ROUTES.gettingStarted,
                },
                ROUTES.gettingStarted,
              )
            }
          />
          {savedViews.map((view) => {
            const href = viewHref(view);
            return (
              <Row
                key={view.id}
                id={`view:${view.id}`}
                icon={viewIcon(view.icon)}
                label={view.name}
                sublabel={`Saved view · ${view.entity}`}
                keywords={[view.name, 'view', 'saved', view.entity]}
                onSelect={() =>
                  go(
                    {
                      id: `view:${view.id}`,
                      kind: 'nav',
                      label: view.name,
                      sublabel: 'Saved view',
                      to: href,
                    },
                    href,
                  )
                }
              />
            );
          })}
        </Group>

        {searching && results && results.inquiries.length > 0 ? (
          <Group heading="Inquiries">
            {results.inquiries.map((inquiry) => {
              const to = ROUTES.inquiry(inquiry.id);
              const stage = stageMeta(inquiry.stageKey);
              const context = inquiry.company?.name ?? inquiry.contact?.fullName ?? inquiry.type;
              return (
                <Row
                  key={inquiry.id}
                  id={`inquiry:${inquiry.id}`}
                  icon={FileText}
                  iconColor={stage.accent}
                  label={<Highlight text={inquiry.subject} query={term} />}
                  sublabel={
                    <>
                      <span className="font-mono">{inquiry.ref}</span>
                      {` · ${stage.label} · `}
                      <Highlight text={context} query={term} />
                    </>
                  }
                  keywords={[inquiry.subject, inquiry.ref, context, term]}
                  onSelect={() =>
                    go(
                      {
                        id: `inquiry:${inquiry.id}`,
                        kind: 'inquiry',
                        label: inquiry.subject,
                        sublabel: inquiry.ref,
                        to,
                      },
                      to,
                    )
                  }
                />
              );
            })}
          </Group>
        ) : null}

        {searching && results && results.contacts.length > 0 ? (
          <Group heading="Contacts">
            {results.contacts.map((contact) => {
              const to = ROUTES.contact(contact.id);
              const detail = [contact.title, contact.company?.name ?? contact.email]
                .filter(Boolean)
                .join(' · ');
              return (
                <Row
                  key={contact.id}
                  id={`contact:${contact.id}`}
                  icon={UserIcon}
                  label={<Highlight text={contact.fullName} query={term} />}
                  sublabel={<Highlight text={detail} query={term} />}
                  keywords={[contact.fullName, contact.email, detail, term]}
                  onSelect={() =>
                    go(
                      {
                        id: `contact:${contact.id}`,
                        kind: 'contact',
                        label: contact.fullName,
                        sublabel: contact.company?.name ?? contact.email,
                        to,
                      },
                      to,
                    )
                  }
                />
              );
            })}
          </Group>
        ) : null}

        {searching && results && results.companies.length > 0 ? (
          <Group heading="Companies">
            {results.companies.map((company) => {
              const to = ROUTES.company(company.id);
              const detail = [company.segment, company.city, company.domain]
                .filter(Boolean)
                .join(' · ');
              return (
                <Row
                  key={company.id}
                  id={`company:${company.id}`}
                  icon={Building2}
                  label={<Highlight text={company.name} query={term} />}
                  sublabel={<Highlight text={detail} query={term} />}
                  keywords={[company.name, detail, term]}
                  onSelect={() =>
                    go(
                      {
                        id: `company:${company.id}`,
                        kind: 'company',
                        label: company.name,
                        sublabel: company.segment ?? company.domain ?? undefined,
                        to,
                      },
                      to,
                    )
                  }
                />
              );
            })}
          </Group>
        ) : null}

        {searching && results && results.users.length > 0 ? (
          <Group heading="People">
            {results.users.map((user) => {
              const to = `${ROUTES.settings}/team`;
              return (
                <Row
                  key={user.id}
                  id={`user:${user.id}`}
                  icon={Users}
                  label={<Highlight text={user.name} query={term} />}
                  sublabel={user.title ?? user.email}
                  keywords={[user.name, user.email, term]}
                  onSelect={() =>
                    go({ id: `user:${user.id}`, kind: 'nav', label: user.name, to }, to)
                  }
                />
              );
            })}
          </Group>
        ) : null}

        {searching && results && results.tags.length > 0 ? (
          <Group heading="Tags">
            {results.tags.map((tag) => {
              const to = `${ROUTES.inbox}?tag=${encodeURIComponent(tag.id)}`;
              return (
                <Row
                  key={tag.id}
                  id={`tag:${tag.id}`}
                  icon={TagIcon}
                  iconColor={tagColorMeta(tag.color).accent}
                  label={<Highlight text={tag.name} query={term} />}
                  sublabel={
                    typeof tag.count === 'number'
                      ? `${tag.count} tagged ${tag.count === 1 ? 'inquiry' : 'inquiries'}`
                      : 'Tag'
                  }
                  keywords={[tag.name, 'tag', term]}
                  onSelect={() => go({ id: `tag:${tag.id}`, kind: 'nav', label: tag.name, to }, to)}
                />
              );
            })}
          </Group>
        ) : null}

        {searching && search.isFetching && resultCount === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-faint">Searching…</p>
        ) : null}
      </Command.List>

      {/* Footer */}
      <div className="flex h-9 shrink-0 items-center justify-between gap-4 border-t border-line-subtle bg-surface-2 px-3">
        <MatchCount searching={searching} />
        <span className="flex items-center gap-3 text-xs text-muted">
          <FooterHint keys={['arrowup', 'arrowdown']}>Navigate</FooterHint>
          <FooterHint keys={['enter']}>Open</FooterHint>
          <FooterHint keys={['escape']}>Close</FooterHint>
        </span>
      </div>
    </Command.Dialog>
  );
}

/** Reads cmdk's own filtered count — must live inside the Command context. */
function MatchCount({ searching }: { searching: boolean }) {
  const count = useCommandState((state) => state.filtered.count);
  return (
    <span className="text-xs text-faint">
      {searching ? (
        <>
          <span className="tnum text-muted">{count}</span> match{count === 1 ? '' : 'es'}
        </>
      ) : (
        'Type to search every record'
      )}
    </span>
  );
}

function FooterHint({ keys, children }: { keys: string[]; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1">
      <Kbd keys={keys} size="sm" />
      {children}
    </span>
  );
}

async function downloadInquiriesCsv(): Promise<void> {
  const blob = await api.exportCsv();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `alphaform-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
